import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getFirebaseAuth, isDevBypass, isMisconfigured, signOutFirebase } from '../src/lib/firebase';
import { adminApi, ApiError } from './admin-api';
import './admin-users-page';
import './admin-invitations-page';
import '../src/pages/login-page';

/**
 * Root element + the single shell-level gate for the admin console.
 *
 * admin.rdx.tools is a SEPARATE Firebase Hosting site; its ROOT (/) IS this
 * console. There is no router — the whole app is one page behind an auth +
 * admin-role gate. The FE gate is UX (the server enforces admin on every
 * `/api/admin/*` call), but we still gate properly.
 *
 * Boot states, decided in order (mirrors the decks shell's <deck-app> gate):
 *
 *   1. misconfigured — NOT localhost AND no Firebase config → fail closed,
 *      render a blocked panel (never a silent bypass).
 *   2. dev-bypass    — localhost AND no config → treat as admin (the decks app
 *      does the same; the Go FakeVerifier accepts 'dev-token-local').
 *   3. otherwise     — await Firebase + the first onAuthStateChanged:
 *        • no user           → render the firewall-safe <login-page> (reused
 *          verbatim from the decks app; after sign-in it does
 *          signInWithCustomToken + a full-page reload to "/", which on
 *          admin.rdx.tools re-bootstraps this console).
 *        • user present      → GET /api/admin/me:
 *            – role === 'admin' → render <admin-users-page>.
 *            – otherwise        → render a "no admin access" panel + sign-out.
 *
 * Light-DOM (no shadow root) so admin.css + the Paper tokens loaded by
 * index.html cascade into the page + the reused login element.
 */
type Phase = 'loading' | 'login' | 'blocked' | 'admin' | 'forbidden';
type Tab = 'users' | 'invitations';

@customElement('admin-app')
export class AdminApp extends LitElement {
  /** Resolved boot phase. Starts 'loading' so nothing protected paints. */
  @state() private phase: Phase = 'loading';
  /** The email of the signed-in (possibly non-admin) user, for the header /
   *  forbidden panel. */
  @state() private email = '';
  /** True while sign-out is in flight (disables the button). */
  @state() private signingOut = false;
  /** Active console tab. Only meaningful in the 'admin' phase. */
  @state() private tab: Tab = 'users';

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.evaluate();
  }

  /** Resolve the boot phase once. See the class doc for the decision order. */
  private async evaluate(): Promise<void> {
    if (isMisconfigured()) {
      this.phase = 'blocked';
      return;
    }
    if (isDevBypass()) {
      // Localhost, no config: treated as an admin so the console is usable in
      // local dev without a Firebase project. /api/admin/me still runs to fill
      // in the header identity (the dev server's FakeVerifier answers it).
      await this.confirmAdmin('dev@ricoh-europe.com');
      return;
    }
    try {
      const auth = await getFirebaseAuth();
      if (!auth) {
        // Not dev-bypass / misconfigured but no instance — fail to login
        // rather than hang on the splash.
        this.phase = 'login';
        return;
      }
      const { onAuthStateChanged } = await import('firebase/auth');
      // Resolve on the first emission; tear the listener down immediately
      // (sign-in does a full reload, so one-shot resolution is enough).
      const user = await new Promise<{ email: string | null } | null>((resolve) => {
        const unsub = onAuthStateChanged(auth, (u) => {
          unsub();
          resolve(u);
        });
      });
      if (!user) {
        this.phase = 'login';
        return;
      }
      await this.confirmAdmin(user.email ?? '');
    } catch {
      // Firebase failed to load (network blip): fail to login, never to an
      // open console.
      this.phase = 'login';
    }
  }

  /** With a Firebase session in hand, ask the server who we are. role==='admin'
   *  → the console; anything else (incl. a 403) → the forbidden panel. */
  private async confirmAdmin(fallbackEmail: string): Promise<void> {
    this.email = fallbackEmail;
    try {
      const me = await adminApi.me();
      this.email = me.email || fallbackEmail;
      this.phase = me.role === 'admin' ? 'admin' : 'forbidden';
    } catch (err) {
      // A 401/403 here means a valid Firebase user who is not an admin (or whose
      // session the server rejected) — show the forbidden panel, not a blank
      // page. Any other error is treated the same: deny access.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        this.phase = 'forbidden';
        return;
      }
      this.phase = 'forbidden';
    }
  }

  private signOut = async (): Promise<void> => {
    if (this.signingOut) return;
    this.signingOut = true;
    try {
      await signOutFirebase();
    } finally {
      // Full reload — the gate re-runs from scratch and resolves to 'login'.
      window.location.assign('/');
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  render(): TemplateResult {
    switch (this.phase) {
      case 'loading':
        return this.splash();
      case 'blocked':
        return this.blocked();
      case 'login':
        // Reuse the decks app's firewall-safe sign-in element verbatim. After a
        // successful sign-in it exchanges the custom token and full-reloads to
        // "/", which on admin.rdx.tools re-bootstraps this console → 'admin'.
        return html`<login-page></login-page>`;
      case 'forbidden':
        return this.forbidden();
      case 'admin':
        return this.console();
    }
  }

  private console(): TemplateResult {
    return html`
      <header class="admin-topbar">
        <div class="admin-brand">
          <span class="brand-mark" aria-hidden="true">◆</span>
          <span class="brand-name">Admin</span>
          <span class="brand-sub">Workplace by Ricoh</span>
        </div>
        <nav class="admin-tabs" aria-label="Admin sections">
          <button
            class="admin-tab"
            type="button"
            aria-current=${this.tab === 'users' ? 'page' : 'false'}
            @click=${() => (this.tab = 'users')}
          >
            Users
          </button>
          <button
            class="admin-tab"
            type="button"
            aria-current=${this.tab === 'invitations' ? 'page' : 'false'}
            @click=${() => (this.tab = 'invitations')}
          >
            Invitations
          </button>
        </nav>
        <div class="admin-whoami">
          <span class="whoami-email" title=${this.email}>${this.email}</span>
          <button
            class="btn-secondary signout"
            type="button"
            ?disabled=${this.signingOut}
            @click=${this.signOut}
          >
            ${this.signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </header>
      <main class="admin-main">
        ${this.tab === 'users'
          ? html`<admin-users-page .meEmail=${this.email}></admin-users-page>`
          : html`<admin-invitations-page></admin-invitations-page>`}
      </main>
    `;
  }

  private forbidden(): TemplateResult {
    return html`
      <div class="admin-center">
        <div class="admin-card">
          <p class="eyebrow">Admin console</p>
          <h1>You don’t have admin access</h1>
          <p class="card-text">
            ${this.email
              ? html`You’re signed in as <strong>${this.email}</strong>, but this account isn’t an
                  administrator.`
              : html`This account isn’t an administrator.`}
            Ask an existing admin to grant you access, or sign in with a different account.
          </p>
          <button
            class="btn-primary"
            type="button"
            ?disabled=${this.signingOut}
            @click=${this.signOut}
          >
            ${this.signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </div>
    `;
  }

  /** Minimal, chrome-free loading state. */
  private splash(): TemplateResult {
    return html`
      <div class="admin-splash" role="status" aria-live="polite">Loading…</div>
    `;
  }

  /** Hard, fail-closed panel for a misconfigured deployment. */
  private blocked(): TemplateResult {
    return html`
      <div class="admin-center">
        <div class="admin-card" role="alert">
          <h1>Sign-in unavailable</h1>
          <p class="card-text">Authentication is not configured for this deployment.</p>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'admin-app': AdminApp;
  }
}
